import { defineComponent as _defineComponent } from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import { useEventQuery } from '@/enitites/event/api/queries'
import { useImage } from '@/shared/composables/image'
import { computed, ref, watch } from 'vue'
// @ts-expect-error Not a ts file
import store from '@/store'
import { useCartMutation, useCheckoutMutation, useTicketsForEventQuery } from '@/features/tickets/api/queries'
import PurchaseTicketItem from '@/features/tickets/ui/PurchaseTicketItem.vue'
import OrderSummary from '@/features/tickets/ui/OrderSummary.vue'
import BKInput from '@/components/AppShared/BKInput.vue'
import useValidationRules from '@/shared/composables/validation'
import { useCart } from '@/features/tickets/composables/useCart'
import { usePricing } from '@/features/tickets/composables/usePricing'
import { useRouter } from 'vue-router/composables'


export default /*#__PURE__*/_defineComponent({
  __name: 'PurchaseTicketsInterface',
  props: {
    eventId: null
  },
  setup(__props: any) {

const props = __props as {
  eventId: number
};



const { getImageById } = useImage()
const rules = useValidationRules()
const getUserDB = computed(() => store.getters.getUserDB)
const { data: event, isLoading: isLoadingEvent, isError: isEventError } = useEventQuery(props.eventId)
const { data: tiers, isLoading: isLoadingTickets, isError: isTicketsError } = useTicketsForEventQuery(props.eventId)
const { mutate: postCart, isPending: isPostingCart, error: errorPostingCart, data: cartSummary } = useCartMutation()
const { mutate: postCheckout, isPending: isPostingCheckout, error: errorPostingCheckout } = useCheckoutMutation()

const STEPS = {
  CHOOSE_TICKETS: 'Choose tickets',
  CHECKOUT: 'Check out',
} as const

const currentStep = ref<keyof typeof STEPS>('CHOOSE_TICKETS')
const isFormValid = ref(true)
const discountCode = ref('')
const tickets = computed(() => tiers.value?.tiers || [])
const stripeError = ref<string | null>(null)
const isPaymentInitialized = ref(false)

// Use composables for cart and pricing logic
const { cart, updateCart, isCartEmpty, updateDiscountCode, updateContactInfo } = useCart()
const { getTicketById } = usePricing(tickets, cart)

const router = useRouter()

// Initialize cart with user data if available
if (getUserDB.value?.aEmail || getUserDB.value?.fullName) {
  updateContactInfo(getUserDB.value?.aEmail || getUserDB.value?.parentEmail || '', getUserDB.value?.fullName || '')
}

const isCheckoutDisabled = computed(() => isCartEmpty.value)

const initPaymentElement = async (clientSecret: string) => {
  isPaymentInitialized.value = true

  let stripe
  try {
    stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
  } catch (error) {
    stripeError.value = (error as Error)?.message || 'An error occurred while loading payment gateway.'
    isPaymentInitialized.value = false
    return
  }

  if (!stripe) {
    stripeError.value = 'Failed to load payment gateway.'
    return
  }

  let checkout
  try {
    stripeError.value = null
    checkout = await stripe.initEmbeddedCheckout({ clientSecret })
  } catch (error) {
    stripeError.value = (error as Error)?.message || 'An error occurred while initializing payment.'
    isPaymentInitialized.value = false
    return
  }

  if (!checkout) {
    stripeError.value = 'Failed to initialize payment elements.'
    return
  }

  checkout.mount('#payment-element')
}

const handlePurchase = () => {
  if (!isFormValid.value || isPostingCart.value || !cartSummary.value || isPostingCheckout.value) return

  postCheckout(
    {
      eventId: props.eventId,
      data: {
        ...cartSummary.value,
        contactEmail: cart.value.contactEmail,
        contactName: cart.value.contactName,
      },
    },
    {
      onSuccess: (response) => {
        if ('clientSecret' in response && response.clientSecret) {
          try {
            stripeError.value = null
            initPaymentElement(response.clientSecret)
          } catch (error) {
            stripeError.value = (error as Error)?.message || 'An error occurred while initializing payment.'
          }
        } else if (event.value) {
          router.replace({ name: 'PurchaseComplete', params: { id: event.value.id.toString() } })
        }
      },
    }
  )
}

const handleApplyDiscountCode = () => {
  updateDiscountCode(discountCode.value)

  if (cart.value.tiers.length > 0) {
    postCart(
      {
        eventId: props.eventId,
        data: cart.value,
      },
      {
        onSuccess: (response) => {
          discountCode.value = response.discountCode || ''
        },
      }
    )
  }
}

watch(discountCode, (code) => {
  updateDiscountCode(code)
})

watch(
  () => cart.value.tiers,
  (newTiers) => {
    if (newTiers.length > 0) {
      postCart({
        eventId: props.eventId,
        data: {
          tiers: newTiers,
          contactEmail: cart.value.contactEmail,
          contactName: cart.value.contactName,
          discountCode: cart.value.discountCode,
        },
      })
    }
  },
  { deep: true }
)

return { __sfc: true,props, getImageById, rules, getUserDB, event, isLoadingEvent, isEventError, tiers, isLoadingTickets, isTicketsError, postCart, isPostingCart, errorPostingCart, cartSummary, postCheckout, isPostingCheckout, errorPostingCheckout, STEPS, currentStep, isFormValid, discountCode, tickets, stripeError, isPaymentInitialized, cart, updateCart, isCartEmpty, updateDiscountCode, updateContactInfo, getTicketById, router, isCheckoutDisabled, initPaymentElement, handlePurchase, handleApplyDiscountCode, PurchaseTicketItem, OrderSummary, BKInput }
}

})